import { SearchInput } from "@/components/flexkit/SearchInput";
import { AddGroupMembersDialog } from "@/components/settings/organization/groups/AddGroupMembersDialog";
import { EditGroupDialog } from "@/components/settings/organization/groups/EditGroupDialog";
import { GroupCard } from "@/components/settings/organization/groups/GroupCard";
import type { PlainMessage } from "@bufbuild/protobuf";
import type { Group } from "gitpod-next-api/gitpod/v1/group_pb";
import { useState, useMemo, type FC } from "react";

type GroupsListProps = {
    groups: PlainMessage<Group>[];
};

export const GroupsList: FC<GroupsListProps> = ({ groups }) => {
    const [searchValue, setSearchValue] = useState("");
    const [showAddMembersDialog, setShowAddMembersDialog] = useState<{ group: PlainMessage<Group> } | undefined>();
    const [showEditDialog, setShowEditDialog] = useState<{ group: PlainMessage<Group> } | undefined>();

    const filteredGroups = useMemo(() => {
        // Filter out system-managed groups
        const userGroups = groups.filter((group) => !group.systemManaged);

        if (!searchValue.trim()) return userGroups;

        const query = searchValue.toLowerCase();
        return userGroups.filter((group) => group.name.toLowerCase().includes(query));
    }, [groups, searchValue]);

    return (
        <div className="space-y-4">
            {/* Search Input */}
            <SearchInput
                wrapperClassName="w-full"
                className="max-w-none"
                placeholder="Search by name or keyword"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onClear={() => setSearchValue("")}
                data-tracking-id="groups-search"
            />

            {/* Groups Card Grid */}
            <div className="grid grid-cols-[repeat(auto-fill,minmax(320px,1fr))] gap-4">
                {filteredGroups.map((group) => (
                    <GroupCard
                        key={group.id}
                        group={group}
                        onAddMembers={() => setShowAddMembersDialog({ group })}
                        onEdit={() => setShowEditDialog({ group })}
                    />
                ))}
            </div>

            {/* Add group members */}
            {showAddMembersDialog?.group && (
                <AddGroupMembersDialog
                    open={true}
                    onClose={() => setShowAddMembersDialog(undefined)}
                    group={showAddMembersDialog.group}
                />
            )}

            {/* Edit group */}
            {showEditDialog?.group && (
                <EditGroupDialog
                    open={true}
                    onClose={() => setShowEditDialog(undefined)}
                    group={showEditDialog.group}
                />
            )}
        </div>
    );
};
